// The strings `appErrorMessage` returns — kept apart from the switch so the
// copy can be read, reviewed and translated as prose, and so a missing case
// shows up as a missing *key* rather than an inline literal buried in a switch.
//
// W39.54. Every one of these is a code that used to reach a user as the
// backend's terse string. The standard applied throughout: state the fact, then
// say what the person can do about it. 「找不到相簿」 is true and leaves the user
// stuck; 「這本相簿可能已被刪除, 或連結已失效, 請回到相簿列表重新選擇」 is the same
// fact with an exit.
//
// Kept in one object (not a const-per-string) because the backend contract test
// parses `case "…":` out of the mapper files, and a flat table is what a
// translator reads top-to-bottom.

import { MAX_UPLOAD_FILES, MAX_UPLOAD_BYTES } from "@/lib/apiLimits";
import { CALENDAR_RESTORE_DAYS } from "@/lib/retentionWindows";

export const APP_ERROR_I18N = {
  /** Last resort: a code with no case. Says what happened, not what failed. */
  generic: "操作未能完成, 請稍後再試。",

  // --- 相簿 ---------------------------------------------------------------
  album_not_found: "這本相簿可能已被刪除, 或連結已失效。請回到相簿列表重新選擇。",
  // W40.15. Arrived with the exception of the same name: until W40.15 the album
  // module answered an expired restore with `ALBUM_NOT_FOUND`, so this had no
  // case to be written for. The wording deliberately is **not** the not-found
  // one above — the album is in the trash with a countdown on it; what has
  // happened is that the window closed. State that, then say what is still
  // available, which is the standard this file applies throughout.
  album_restore_window_expired:
    "這本相簿已經超過保留期，無法還原。它還在垃圾桶裡，但到了時間就會被永久刪除。",
  album_forbidden: "你只能操作自己參與的配對裡的相簿, 這本不屬於你們。",
  album_name_taken: "你們已經有一本用這個名字的相簿了, 換一個名字就好。",
  album_reserved_name: "「未分類」是系統保留的名稱, 請換一個名字。",
  album_system_delete: "「未分類」是系統相簿, 不能刪除。照片可以從裡面移走。",
  album_system_rename: "「未分類」是系統相簿, 不能重新命名。",
  album_cover_not_in_album: "封面必須是這本相簿裡的照片。如果那張已經被移走, 請改選一張, 或改用「自動封面」。",
  album_item_not_found: "這張照片可能已經從相簿移走了。重新載入看看。",
  album_invalid_position: "有另一張照片已經排在這個位置, 請換一個順序。",

  // --- 上傳 ---------------------------------------------------------------
  // The number is the backend's per-file cap (`album.storage.MAX_BYTES`) and
  // w3.md §5.2's, which nginx now allows through. W50.1 made this name the
  // 25 MB *request* ceiling instead, because nginx's `client_max_body_size`
  // sat below the backend's — a number that was itself a stale comment about
  // the backend rather than a decision (W52.5, and nginx is now 52 MB).
  // Two sentences, one limit: `apiLimits.test.ts` pins them to the constant.
  upload_too_large: `檔案超過 ${MAX_UPLOAD_BYTES / 1048576} MB 上限。可以用「壓縮」或改傳小一點的版本嗎?`,
  upload_too_many: `一次最多上傳 ${MAX_UPLOAD_FILES} 個檔案, 剩下的分批傳就好。`,
  upload_bad_type: "這個格式不支援。請改用 JPEG、PNG、WebP、HEIC 或影片檔。",
  media_not_found: "找不到這個檔案, 它可能已被刪除。",
  media_forbidden: "只有上傳者可以刪除這個檔案。",

  // --- 紀念日 -------------------------------------------------------------
  anniversary_not_found: "找不到這個紀念日, 它可能已被刪除。",
  anniversary_forbidden: "這個紀念日不屬於你們的配對。",
  anniversary_validation: "這個紀念日還有欄位沒有填好, 請檢查日期和標題。",
  anniversary_date_range: "日期超出範圍。請確認年份沒有填錯。",
  anniversary_custom_label_required: "自訂紀念日需要一個名稱, 請補上。",
  anniversary_invalid_reminder: "提醒天數要介於 0 到 30 之間, 請調整一下。",
  anniversary_milestone_not_found: "找不到這個週年或里程碑記錄。",
  anniversary_anchor_date_locked: "起始日的日期不能直接改。要移動整個紀念日, 請改用其他欄位。",
  // W39.88 — the diary twin of this key is `classify_mood_unavailable` in
  // `diary.ts`; the Laya SUGGEST feature is off by default (project-overview #19)
  // and a 503 is what it answers when that is why.
  anniversary_feature_unavailable: "標題建議功能暫時無法使用",
  anniversary_anchor_field_locked: "這個欄位由起始日自動帶出, 不能單獨修改。",
  anniversary_anchor_recurring_locked: "重複規則跟著起始日走, 要改請從起始日那裡改。",
  anniversary_anchor_delete: "起始日不能刪除, 但可以改名字或設為停用。",
  anniversary_anchor_manual_create_forbidden: "這種起始日由系統自動建立, 不能手動新增。",
  anniversary_auto_field_locked: "這是自動產生的紀念日, 這個欄位不能手動改。",
  anniversary_auto_delete: "這是自動產生的紀念日, 會隨著來源一起變更, 不能單獨刪除。",
  restore_window_expired: "已超過可恢復的期限, 這筆記錄已經被永久刪除了。",

  // --- 日曆 ---------------------------------------------------------------
  calendar_event_not_found: "找不到這個行程, 它可能已被刪除。",
  calendar_forbidden: "這個行程不屬於你們的配對, 或不是你建立的。",
  calendar_invalid_range: "結束日期不能早於開始日期, 請調整一下。",
  calendar_range_too_long: "這個跨度的行程太長了, 請縮短或拆成幾個。",
  // W53.1 D-2. One message for all three refusals — the row is gone, it is
  // someone else's, or it is private — because the server cannot tell them apart
  // and this copy must not imply it can. 「找不到」 is what the user can act on;
  // naming the real reason would confirm a probed id exists.
  calendar_link_target_not_found: "找不到這筆記錄, 可能已被刪除。",
  // W40.12. The 7 here is **right** — the calendar's restore window is a real
  // 7 days, written inline in `calendar/service/crud.py:324` — but it was a
  // literal beside a constant file, which is the arrangement that let the diary
  // sentence keep saying 7 after the diary moved to 30. Derived from
  // `CALENDAR_RESTORE_DAYS` so the next move of either number has one place.
  calendar_restore_window: `已超過 ${CALENDAR_RESTORE_DAYS} 天的復原期限, 這個行程已經無法復原了。`,

  // --- 心願 ---------------------------------------------------------------
  wish_not_found: "找不到這個心願, 它可能已被刪除。",
  wish_forbidden: "這個心願不屬於你們的配對。",
  wish_private: "這個心願設為私人, 只有建立者看得到。",
  wish_invalid_category: "這個分類不存在, 請從清單裡選一個。",
  wish_invalid_completion_mode: "完成方式不正確, 請重新選擇。",
  wish_not_your_log: "這不是你寫的完成紀錄, 只有作者可以修改。",
  wish_block_not_found: "找不到這個區塊, 它可能已經被移除了。",
  wish_block_title_required: "區塊需要一個標題, 請補上。",
  wish_reorder_incomplete: "排序資料不完整, 請重新整理一次全部的區塊。",
  wish_completion_sources_duplicate: "來源重複了, 請移除重複的那一筆。",
  wish_album_item_not_found: "這張照片可能已經從相簿移走了, 請重新挑選。",
  wish_album_item_duplicate: "這張照片已經在這個區塊裡了。",
  wish_restore_window: "已超過可恢復的期限, 這個心願已經被永久刪除了。",

  // --- 個人資料 -----------------------------------------------------------
  profile_not_found: "找不到這個個人資料。",
  profile_validation: "個人資料還有欄位沒有填好, 請檢查後再送出。",
  profile_partner_forbidden: "只能查看伴侶的個人資料。",
  profile_bio_too_long: "個人簡介超過 200 字, 幫我精簡一點嗎?",
  profile_avatar_invalid: "這張圖片不適合當頭像, 請換一張。",
  profile_avatar_not_owned: "只能用你上傳的頭像, 請重新上傳。",
  profile_non_leap_required: "2 月 29 日的生日需要指定在平年是哪一天慶祝。",
  profile_non_leap_not_applicable: "只有 2 月 29 日的生日需要設定平年替代日。",
  profile_non_leap_invalid: "這個平年日期不存在, 請改選 2 月 28 日或 3 月 1 日。",

  // --- 每日留言 / 心情 -----------------------------------------------------
  daily_message_not_found: "找不到這則留言, 它可能已被刪除。",
  daily_message_forbidden: "這則留言不是你的, 不能修改。",
  daily_message_date_invalid: "留言日期不合法, 請確認是 2014 年之後的今天以前。",
  mood_checkin_invalid: "這個心情不在可選範圍內, 請重新選一個。",
  mood_checkin_forbidden: "只能記錄自己的心情。",
  mood_checkin_date_invalid: "日期不合法, 今天只能記錄今天的心情。",

  // --- 首頁 / 日記 --------------------------------------------------------
  dashboard_no_pair: "你們還沒建立配對。完成配對後首頁才會有內容。",
  diary_like_own: "不能按自己的日記讚。",
  diary_tag_not_found: "找不到這個標籤, 請從建議的標籤裡選一個。",
} as const;

export type AppErrorI18nKey = keyof typeof APP_ERROR_I18N;
